import { supabase } from "./supabase";
import type { Escrita } from "./conteudo";

// O livro-caixa do MEI Pro — o lado do contador (000077).
//
// Tudo aqui passa por RPC: as tabelas `mei_*` não são legíveis pela Data API
// (`revoke all`), e quem decide se o contador pode é `mei_posso_escriturar`,
// que reusa `conteudo_liberado('mei-pro')` — a MESMA regra que libera o card e
// o conteúdo. Este módulo não decide acesso: ele transporta.
//
// O VALOR viaja como número (`1234.56`), nunca como o texto mascarado — é a
// cicatriz do campo de moeda do orçamento: `"R$ 1.234,56"` numa coluna numeric
// vira nulo em silêncio. Quem converte é `mascararMoeda`, na fronteira da tela.

export type Natureza = "entrada" | "compra" | "despesa" | "retirada";
export type FormaPagamento =
  | "dinheiro" | "boleto" | "cartao_debito" | "cartao_credito" | "cheque" | "pix" | "outros";
export type Setor = "comercio" | "industria" | "servico" | "transporte";

export const NATUREZAS: { valor: Natureza; rotulo: string }[] = [
  { valor: "entrada", rotulo: "Entrada" },
  { valor: "compra", rotulo: "Compra" },
  { valor: "despesa", rotulo: "Despesa" },
  { valor: "retirada", rotulo: "Retirada" },
];

export const FORMAS: { valor: FormaPagamento; rotulo: string }[] = [
  { valor: "pix", rotulo: "Pix" },
  { valor: "dinheiro", rotulo: "Dinheiro" },
  { valor: "boleto", rotulo: "Boleto" },
  { valor: "cartao_debito", rotulo: "Cartão de débito" },
  { valor: "cartao_credito", rotulo: "Cartão de crédito" },
  { valor: "cheque", rotulo: "Cheque" },
  { valor: "outros", rotulo: "Outros" },
];

export const SETORES: { valor: Setor; rotulo: string }[] = [
  { valor: "comercio", rotulo: "Comércio" },
  { valor: "industria", rotulo: "Indústria" },
  { valor: "servico", rotulo: "Serviço" },
  { valor: "transporte", rotulo: "Transporte" },
];

export type ClienteMei = {
  id: string; // o vínculo (mei_clientes.id) — é o que atualizar/remover recebem
  cliente_id: string; // a pessoa (profiles.id) — é o que o livro recebe
  nome: string | null;
  email: string | null;
  cnpj: string | null;
  tem_login: boolean;
  convidado_em: string | null; // o último convite que saiu (trilha da 000080)
  /**
   * O teto do ANO CORRENTE, já com o proporcional do ano de abertura e o teto
   * do caminhoneiro aplicados (`mei_teto`, 000085). A tela pinta a barra com
   * este número — nunca recalcula: seria a segunda definição do mesmo teto.
   */
  limite: number;
  receita_no_ano: number;
  lancamentos: number;
  ultimo_lancamento: string | null;
  /** FATO do MEU vínculo — é o que o formulário de edição preenche e salva. */
  caminhoneiro: boolean;
  /** FATO do MEU vínculo: primeiro dia do mês de abertura, ou nulo. */
  abertura: string | null;
  /**
   * O PORQUÊ do `limite` acima, vindo de quem o calculou (`mei_teto_detalhe`,
   * 000091). Não é o mesmo que os fatos do meu vínculo: nos clientes com dois
   * contadores o teto é agregado, e quem declarou pode ter sido o outro.
   * `teto_manual` significa que um override venceu — aí nenhuma regra legal
   * explica o número, e os outros dois vêm zerados.
   */
  teto_manual: boolean;
  teto_caminhoneiro: boolean;
  /** Mês da abertura quando o teto do ano corrente é proporcional; nulo se cheio. */
  teto_mes_abertura: number | null;
};

/** Os doze meses, para o seletor de abertura. */
export const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

/**
 * "Abril de 2026" a partir de `2026-04-01`. A abertura é sempre dia 1 (o check
 * da 000085 garante), então o dia não aparece: mostrar "01/04/2026" faria o
 * contador achar que o hub sabe o dia exato da abertura, que ele não sabe.
 */
export function mesDeAbertura(iso: string): string {
  const [ano, mes] = iso.split("-");
  return `${MESES[Number(mes) - 1]} de ${ano}`;
}

/**
 * A frase que explica um teto — APRESENTAÇÃO do que o banco decidiu. Recebe os
 * dois sinais prontos (`caminhoneiro` e o mês da abertura, nulo quando o teto é
 * cheio) e não calcula nada: comparar ano com ano aqui seria um pedaço da regra
 * do teto vivendo em JavaScript, e a regra mora em `mei_teto` (000085).
 *
 * Serve as três telas — a lista do contador, o livro e a tela do cliente MEI —,
 * então a frase é uma só em todo o hub.
 */
export function motivoDoTeto(sinais: {
  /** Um override venceu: nenhuma regra legal explica o número. */
  manual?: boolean;
  caminhoneiro: boolean;
  mesDeAberturaProporcional: number | null;
}): string | null {
  // O override vem PRIMEIRO e cala os outros: dizer "proporcional — abriu em
  // junho" ao lado de um número que veio de um ajuste à mão é explicação errada,
  // e explicação errada ensina o contador a desconfiar do número certo.
  if (sinais.manual) return "limite ajustado à mão";
  const partes: string[] = [];
  if (sinais.caminhoneiro) partes.push("MEI caminhoneiro");
  const mes = sinais.mesDeAberturaProporcional;
  if (mes !== null && mes >= 1 && mes <= 12) {
    partes.push(`proporcional — abriu em ${MESES[mes - 1].toLowerCase()}`);
  }
  return partes.length > 0 ? partes.join(" · ") : null;
}

export type Lancamento = {
  id: string;
  data: string;
  natureza: Natureza;
  valor: number;
  descricao: string | null;
  comprador: string | null;
  forma_pagamento: FormaPagamento | null;
  setor: Setor | null;
  recebido: boolean;
  anexo_path: string | null;
};

export type FaturamentoAno = {
  ano: number;
  receita: number;
  compras: number;
  despesas: number;
  retiradas: number;
  limite: number;
  limite_compras: number;
  /** Um override do vínculo venceu — nenhuma regra legal explica este teto. */
  teto_manual: boolean;
  caminhoneiro: boolean;
  /**
   * O mês (1–12) em que o MEI abriu, **quando este ano é o da abertura** — ou
   * seja, quando o `limite` desta linha é proporcional. Nulo nos outros anos:
   * é o próprio sinal de "teto cheio", vindo de quem calculou o teto (000091).
   */
  mes_de_abertura: number | null;
};

function falha(mensagem: string | undefined): { ok: false; mensagem: string } {
  return {
    ok: false,
    mensagem: mensagem?.trim()
      ? mensagem
      : "Não foi possível concluir agora. Tente de novo em instantes.",
  };
}

export type MinhaContaMei = {
  sou_cliente: boolean;
  cliente_id: string | null;
  contadores: { nome: string | null; email: string | null }[];
  /**
   * O teto do ANO CORRENTE deste cliente, com o porquê (000091). Existe porque
   * `mei_faturamento` só devolve ano COM lançamento: sem esta fonte, a tela do
   * cliente sem movimento no ano caía no teto de OUTRO ano — medido, 127
   * clientes veriam um teto menor que o real, e 1.087 veriam "de R$ 0,00".
   */
  limite: number;
  teto_manual: boolean;
  teto_caminhoneiro: boolean;
  teto_mes_abertura: number | null;
};

/** A pergunta da tela inicial: esta conta é de um cliente MEI? E de quem? */
export async function minhaConta(): Promise<MinhaContaMei | null> {
  const { data, error } = await supabase.rpc("mei_minha_conta");
  if (error) return null;
  return data as MinhaContaMei;
}

export async function listarClientes(): Promise<ClienteMei[] | null> {
  const { data, error } = await supabase.rpc("mei_meus_clientes");
  if (error) return null;
  return (data ?? []) as ClienteMei[];
}

export async function cadastrarCliente(
  nome: string,
  email: string,
  cnpj: string | null,
  fatos: { caminhoneiro?: boolean; abertura?: string | null } = {},
): Promise<Escrita<string>> {
  const { data, error } = await supabase.rpc("mei_cadastrar_cliente", {
    p_nome: nome,
    p_email: email,
    p_cnpj: cnpj,
    p_caminhoneiro: fatos.caminhoneiro ?? false,
    p_abertura: fatos.abertura ?? null,
  });
  if (error) return falha(error.message);
  return { ok: true, res: data as string };
}

export async function atualizarCliente(
  vinculo: string,
  campos: {
    nome?: string | null;
    cnpj?: string | null;
    limite?: number | null;
    caminhoneiro?: boolean | null;
    /** `null` é "não mexe"; para APAGAR a abertura mande `limparAbertura`. */
    abertura?: string | null;
    limparAbertura?: boolean;
  },
): Promise<Escrita<null>> {
  const { error } = await supabase.rpc("mei_atualizar_cliente", {
    p_vinculo: vinculo,
    p_nome: campos.nome ?? null,
    p_cnpj: campos.cnpj ?? null,
    p_limite: campos.limite ?? null,
    p_caminhoneiro: campos.caminhoneiro ?? null,
    p_abertura: campos.abertura ?? null,
    p_limpar_abertura: campos.limparAbertura ?? false,
  });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}

export async function removerCliente(vinculo: string): Promise<Escrita<null>> {
  const { error } = await supabase.rpc("mei_remover_cliente", { p_vinculo: vinculo });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}

export async function extrato(cliente: string, ano?: number): Promise<Lancamento[] | null> {
  const { data, error } = await supabase.rpc("mei_extrato", {
    p_cliente: cliente,
    p_ano: ano ?? null,
  });
  if (error) return null;
  return (data ?? []) as Lancamento[];
}

export async function faturamento(cliente: string): Promise<FaturamentoAno[] | null> {
  const { data, error } = await supabase.rpc("mei_faturamento", { p_cliente: cliente });
  if (error) return null;
  return (data ?? []) as FaturamentoAno[];
}

export type DadosDoLancamento = {
  data: string;
  natureza: Natureza;
  valor: number;
  descricao: string | null;
  comprador: string | null;
  forma: FormaPagamento | null;
  setor: Setor | null;
  recebido: boolean;
};

export async function lancar(cliente: string, d: DadosDoLancamento): Promise<Escrita<string>> {
  const { data, error } = await supabase.rpc("mei_lancar", {
    p_cliente: cliente,
    p_data: d.data,
    p_natureza: d.natureza,
    p_valor: d.valor,
    p_descricao: d.descricao,
    p_comprador: d.comprador,
    p_forma: d.forma,
    p_setor: d.setor,
    p_recebido: d.recebido,
  });
  if (error) return falha(error.message);
  return { ok: true, res: data as string };
}

export async function atualizarLancamento(
  id: string,
  d: DadosDoLancamento,
): Promise<Escrita<null>> {
  const { error } = await supabase.rpc("mei_atualizar_lancamento", {
    p_id: id,
    p_data: d.data,
    p_natureza: d.natureza,
    p_valor: d.valor,
    p_descricao: d.descricao,
    p_comprador: d.comprador,
    p_forma: d.forma,
    p_setor: d.setor,
    p_recebido: d.recebido,
  });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}

export async function excluirLancamento(id: string): Promise<Escrita<null>> {
  const { error } = await supabase.rpc("mei_excluir_lancamento", { p_id: id });
  if (error) return falha(error.message);
  return { ok: true, res: null };
}

// ---------------------------------------------------------------------------
// O convite (Edge Function convidar-cliente-mei, 000080)
// ---------------------------------------------------------------------------
export type ResultadoDoConvite = "convite_enviado" | "redefinicao_enviada" | "ja_tem_acesso";

/**
 * Pede ao Auth (via Edge Function) o e-mail de convite do cliente. O banco é
 * quem autoriza (`mei_posso_convidar`); o link NUNCA volta para cá — só o
 * desfecho. Nunca lança.
 */
export async function convidarCliente(clienteId: string): Promise<Escrita<ResultadoDoConvite>> {
  try {
    const { data: sessao } = await supabase.auth.getSession();
    const token = sessao.session?.access_token;
    if (!token) return falha("Sessão expirada — recarregue a página.");
    const { data, error } = await supabase.functions.invoke("convidar-cliente-mei", {
      body: { cliente_id: clienteId },
      headers: { Authorization: `Bearer ${token}` },
    });
    if (error) return falha("O convite não pôde ser enviado agora. Tente de novo em instantes.");
    const r = (data as { resultado?: string } | null)?.resultado;
    if (r === "convite_enviado" || r === "redefinicao_enviada" || r === "ja_tem_acesso") {
      return { ok: true, res: r };
    }
    return falha(undefined);
  } catch {
    return falha(undefined);
  }
}

// ---------------------------------------------------------------------------
// O comprovante do lançamento (bucket privado mei-anexos, 000080)
// ---------------------------------------------------------------------------
const EXTENSOES: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/webp": "webp",
  "image/gif": "gif",
  "application/pdf": "pdf",
};

/** O que o campo de arquivo aceita — a mesma lista do bucket. */
export const ACEITA_ANEXO = Object.keys(EXTENSOES).join(",");

/**
 * Sobe o comprovante de um lançamento. O CAMINHO vem do banco
 * (`mei_definir_anexo` monta das chaves — aceitar caminho do front deixaria o
 * anexo apontar para a pasta de outro cliente, a lição do avatar). A ordem é
 * caminho primeiro, arquivo depois: se o upload falhar, o lançamento fica
 * apontando para um objeto ausente até a próxima tentativa — visível e
 * reparável, ao contrário de um arquivo órfão no bucket.
 */
export async function anexarComprovante(lancamentoId: string, arquivo: File): Promise<Escrita<string>> {
  const ext = EXTENSOES[arquivo.type];
  if (!ext) return falha("O comprovante precisa ser imagem (PNG, JPG, WebP, GIF) ou PDF.");
  if (arquivo.size > 10 * 1024 * 1024) return falha("O comprovante pode ter no máximo 10 MB.");

  const { data: caminho, error } = await supabase.rpc("mei_definir_anexo", {
    p_lancamento: lancamentoId,
    p_ext: ext,
  });
  if (error || typeof caminho !== "string") return falha(error?.message);

  const up = await supabase.storage.from("mei-anexos").upload(caminho, arquivo, {
    upsert: true,
    contentType: arquivo.type,
  });
  if (up.error) return falha("O arquivo não subiu. Tente de novo em instantes.");
  return { ok: true, res: caminho };
}

/** Tira o comprovante: limpa no banco e apaga o objeto que ele apontava. */
export async function removerComprovante(lancamentoId: string): Promise<Escrita<null>> {
  const { data: antigo, error } = await supabase.rpc("mei_definir_anexo", {
    p_lancamento: lancamentoId,
    p_ext: null,
  });
  if (error) return falha(error.message);
  if (typeof antigo === "string" && antigo !== "") {
    await supabase.storage.from("mei-anexos").remove([antigo]);
  }
  return { ok: true, res: null };
}

/**
 * Um link temporário para VER o comprovante (o bucket é privado). Uma hora
 * basta: o link é gerado no clique, não guardado.
 */
export async function urlDoComprovante(caminho: string): Promise<string | null> {
  const { data, error } = await supabase.storage.from("mei-anexos").createSignedUrl(caminho, 3600);
  if (error) return null;
  return data.signedUrl;
}
